<template>
  <v-dialog
    :value="dialogVisible"
    max-width="1160"
    persistent
    overlay-color="#0f172a"
    overlay-opacity="0.6"
    content-class="article-dialog-window"
  >
    <v-card class="article-modal-card">
      <div class="modal-head pa-5 d-flex align-center justify-space-between">
        <div>
          <h3 class="modal-title mb-1">
            {{ isEditMode ? 'Edit Article' : 'Create New Article' }}
          </h3>
          <p class="modal-sub mb-0">
            Content saved here appears on the selected branch sites.
          </p>
        </div>
        <button type="button" class="close-icon-btn" @click="handleClose">
          <v-icon size="20" color="#64748b">mdi-close</v-icon>
        </button>
      </div>

      <v-alert
        v-if="apiErrorMsg"
        type="error"
        dense
        dismissible
        class="mx-5 mt-3 mb-0"
        @input="apiErrorMsg = ''"
      >
        {{ apiErrorMsg }}
      </v-alert>

      <div class="modal-body-container">
        
        <div class="left-col" ref="leftCol">
          <div class="section-badge-label mb-3">
            ARTICLE CONTENT & LOCALIZATION
          </div>

          <div class="lang-toggle-wrap mb-4">
            <button
              type="button"
              :class="['lang-toggle-btn', { active: currentLang === 'th', 'has-error': errors.title_th || errors.content_th }]"
              @click="currentLang = 'th'"
            >
              TH · ไทย
              <span v-if="errors.title_th || errors.content_th" class="error-dot">●</span>
            </button>
            <button
              type="button"
              :class="['lang-toggle-btn', { active: currentLang === 'en' }]"
              @click="currentLang = 'en'"
            >
              EN · English
            </button>
          </div>

          <div class="form-group mb-4">
            <label class="form-label">
              ARTICLE TITLE ({{ currentLang.toUpperCase() }})
              <span v-if="currentLang === 'th'" class="required-star">*</span>
            </label>
            <input
              v-if="currentLang === 'th'"
              v-model="form.title_th"
              type="text"
              placeholder="หัวข้อข่าวสารหรือบทความภาษาไทย..."
              class="form-input"
              :class="{ 'input-error': errors.title_th }"
              @input="onTitleInput('th')"
            />
            <input
              v-else
              v-model="form.title_en"
              type="text"
              placeholder="Article title in English..."
              class="form-input"
              @input="onTitleInput('en')"
            />
            <div v-if="currentLang === 'th' && errors.title_th" class="error-text mt-1">
              {{ errors.title_th }}
            </div>
          </div>

          <div class="form-group mb-4">
            <label class="form-label">URL SLUG</label>
            <div class="slug-input-wrap d-flex align-center">
              <span class="slug-prefix">/news/</span>
              <input
                v-model="form.slug"
                type="text"
                placeholder="article-url-slug"
                class="slug-field flex-grow-1"
                @input="onSlugInput"
              />
            </div>
          </div>

          <div class="form-group mb-4">
            <label class="form-label">COVER IMAGE & BANNER</label>
            <div class="cover-image-card d-flex align-center justify-space-between" :class="{ 'card-error': coverImageError }">
              <div class="d-flex align-center flex-grow-1 mr-3">
                <div class="image-thumb-box d-flex align-center justify-center mr-3">
                  <v-img
                    v-if="form.cover_image"
                    :src="form.cover_image"
                    height="100%"
                    width="100%"
                    cover
                  ></v-img>
                  <v-icon v-else color="#ffffff" size="22">mdi-image-outline</v-icon>
                </div>
                <div class="image-info-wrap">
                  <div class="image-name">
                    {{ imageFileName || (form.cover_image ? 'Cover Image Uploaded' : 'No image selected') }}
                  </div>
                  <div class="image-meta">
                    {{ imageFileSize ? `${imageFileSize} · ` : '' }}Recommended 1600 × 900 px (16:9)
                  </div>
                </div>
              </div>
              <div v-if="coverImageError" class="error-text mt-1 font-weight-bold">
                {{ coverImageError }}
              </div>
              <div class="d-flex align-center gap-2">
                <button
                  v-if="form.cover_image"
                  type="button"
                  class="btn-outline-replace text-error mr-2"
                  @click="removeCoverImage"
                >
                  Remove
                </button>
                <button type="button" class="btn-outline-replace" @click="triggerImageUpload">
                  {{ form.cover_image ? 'Replace' : 'Upload' }}
                </button>
              </div>
              <input
                ref="fileInput"
                type="file"
                accept="image/*"
                style="display:none"
                @change="onImageSelected"
              />
            </div>
            <div class="cover-footnote mt-2">
              Recommended 1600 × 900 px, max 1 MB (JPG, PNG)
            </div>
          </div>

          <div class="form-group mb-4">
            <label class="form-label">
              EXCERPT / SUMMARY ({{ currentLang.toUpperCase() }})
            </label>
            <textarea
              v-if="currentLang === 'th'"
              v-model="form.summary_th"
              rows="3"
              class="form-textarea"
              placeholder="สรุปย่อบทความภาษาไทย..."
            ></textarea>
            <textarea
              v-else
              v-model="form.summary_en"
              rows="3"
              class="form-textarea"
              placeholder="Brief summary of the article in English..."
            ></textarea>
          </div>

          <div class="form-group mb-2">
            <label class="form-label">
              BODY CONTENT ({{ currentLang.toUpperCase() }})
              <span v-if="currentLang === 'th'" class="required-star">*</span>
            </label>
            
            <div v-show="currentLang === 'th'">
              <quill-editor
                v-model="form.content_th"
                :options="editorOption"
                class="article-quill-editor"
                :class="{ 'input-error': errors.content_th }"
                @change="errors.content_th = ''"
              ></quill-editor>
              <div v-if="errors.content_th" class="error-text mt-1">
                {{ errors.content_th }}
              </div>
            </div>
            <div v-show="currentLang === 'en'">
              <quill-editor
                v-model="form.content_en"
                :options="editorOption"
                class="article-quill-editor"
              ></quill-editor>
            </div>
          </div>
        </div>

        <div class="right-col" ref="rightCol">
          <div class="section-badge-label mb-3">
            PUBLISHING CONTROLS
          </div>

          <div class="form-group mb-4">
            <label class="form-label">TARGET BRANCH SCOPE</label>
            <div class="select-field-wrap">
              <select v-model="form.org_codename" class="custom-select-box">
                <option v-for="branch in realBranchList" :key="branch.codename" :value="branch.codename">
                  {{ branch.name }}
                </option>
              </select>
              <v-icon size="18" class="select-chevron" color="#64748b">mdi-chevron-down</v-icon>
            </div>
          </div>

          <div class="form-group mb-4">
            <label class="form-label">CATEGORY</label>
            <div class="select-field-wrap">
              <select v-model="form.category_id" class="custom-select-box">
                <option v-for="cat in realCategoryList" :key="cat.id" :value="cat.id">
                  {{ cat.name }}
                </option>
              </select>
              <v-icon size="18" class="select-chevron" color="#64748b">mdi-chevron-down</v-icon>
            </div>
          </div>

          <div class="form-group mb-4">
            <label class="form-label">TAGS (KEYWORDS)</label>
            <v-combobox
              v-model="form.tags"
              multiple
              chips
              small-chips
              deletable-chips
              outlined
              dense
              hide-details
              color="#2563eb"
              :placeholder="form.tags && form.tags.length ? '' : 'Add Tags and press Enter...'"
              class="custom-entity-autocomplete"
              :menu-props="{ contentClass: 'custom-dropdown-menu', offsetY: true }"
            >
              <template v-slot:selection="{ attrs, item, select, selected }">
                <v-chip
                  v-bind="attrs"
                  :input-value="selected"
                  close
                  small
                  class="custom-keyword-chip"
                  @click="select"
                  @click:close="removeFromList(form.tags, item)"
                >
                  <span>{{ typeof item === 'object' ? (item.name || item.text) : item }}</span>
                </v-chip>
              </template>
            </v-combobox>
          </div>

          <div class="form-group mb-4">
            <label class="form-label">RELATED DEPARTMENTS</label>
            <v-autocomplete
              v-model="form.department_ids"
              :items="departmentList"
              item-text="name"
              item-value="id"
              multiple
              chips
              small-chips
              deletable-chips
              outlined
              dense
              hide-details
              color="#2563eb"
              :loading="isLoadingEntities"
              :placeholder="form.department_ids && form.department_ids.length ? '' : 'Search & select departments...'"
              class="custom-entity-autocomplete"
              :menu-props="{ contentClass: 'custom-dropdown-menu', offsetY: true }"
            >
              <template v-slot:selection="{ attrs, item, select, selected }">
                <v-chip
                  v-bind="attrs"
                  :input-value="selected"
                  close
                  small
                  class="custom-keyword-chip"
                  @click="select"
                  @click:close="removeFromList(form.department_ids, item.id)"
                >
                  <span>{{ item.name }}</span>
                </v-chip>
              </template>
            </v-autocomplete>
          </div>

          <div class="form-group mb-4">
            <label class="form-label">RELATED DOCTORS</label>
            <v-autocomplete
              v-model="form.doctor_ids"
              :items="doctorList"
              item-text="name"
              item-value="id"
              multiple
              chips
              small-chips
              deletable-chips
              outlined
              dense
              hide-details
              color="#2563eb"
              :loading="isLoadingEntities"
              :placeholder="form.doctor_ids && form.doctor_ids.length ? '' : 'Search & select doctors...'"
              class="custom-entity-autocomplete"
              :menu-props="{ contentClass: 'custom-dropdown-menu', offsetY: true }"
            >
              <template v-slot:selection="{ attrs, item, select, selected }">
                <v-chip
                  v-bind="attrs"
                  :input-value="selected"
                  close
                  small
                  class="custom-keyword-chip"
                  @click="select"
                  @click:close="removeFromList(form.doctor_ids, item.id)"
                >
                  <span>{{ item.name }}</span>
                </v-chip>
              </template>
            </v-autocomplete>
          </div>

          <div class="form-group mb-4">
            <label class="form-label">RELATED PACKAGES</label>
            <v-autocomplete
              v-model="form.package_ids"
              :items="packageList"
              item-text="name"
              item-value="id"
              multiple
              chips
              small-chips
              deletable-chips
              outlined
              dense
              hide-details
              color="#2563eb"
              :loading="isLoadingEntities"
              :placeholder="form.package_ids && form.package_ids.length ? '' : 'Search & select packages...'"
              class="custom-entity-autocomplete"
              :menu-props="{ contentClass: 'custom-dropdown-menu', offsetY: true }"
            >
              <template v-slot:selection="{ attrs, item, select, selected }">
                <v-chip
                  v-bind="attrs"
                  :input-value="selected"
                  close
                  small
                  class="custom-keyword-chip"
                  @click="select"
                  @click:close="removeFromList(form.package_ids, item.id)"
                >
                  <span>{{ item.name }}</span>
                </v-chip>
              </template>
            </v-autocomplete>
          </div>

          <div class="form-group mb-4">
            <label class="form-label">RELATED CAMPAIGNS</label>
            <v-autocomplete
              v-model="form.campaign_ids"
              :items="campaignList"
              item-text="name"
              item-value="id"
              multiple
              chips
              small-chips
              deletable-chips
              outlined
              dense
              hide-details
              color="#2563eb"
              :loading="isLoadingEntities"
              :placeholder="form.campaign_ids && form.campaign_ids.length ? '' : 'Search & select campaigns...'"
              class="custom-entity-autocomplete"
              :menu-props="{ contentClass: 'custom-dropdown-menu', offsetY: true }"
            >
              <template v-slot:selection="{ attrs, item, select, selected }">
                <v-chip
                  v-bind="attrs"
                  :input-value="selected"
                  close
                  small
                  class="custom-keyword-chip"
                  @click="select"
                  @click:close="removeFromList(form.campaign_ids, item.id)"
                >
                  <span>{{ item.name }}</span>
                </v-chip>
              </template>
            </v-autocomplete>
          </div>

          <div class="form-group mb-4">
            <label class="form-label">DOCTOR / SPECIALIST LINK</label>
            <div class="doctor-link-card d-flex align-center justify-space-between">
              <div class="d-flex align-center">
                <div class="doctor-avatar mr-3" :class="{ 'avatar-unselected': !selectedDoctor }">
                  <v-icon v-if="!selectedDoctor" size="20" color="#94a3b8">mdi-account-outline</v-icon>
                  <img v-else-if="selectedDoctor.image" :src="selectedDoctor.image" class="doctor-avatar-img" />
                  <span v-else>{{ getInitials(selectedDoctor.name) }}</span>
                </div>
                <div>
                  <div class="doctor-name">
                    {{ selectedDoctor ? selectedDoctor.name : 'Link Specialist / Doctor (Optional)' }}
                  </div>
                  <div class="doctor-dept">
                    {{ selectedDoctor ? (selectedDoctor.department || selectedDoctor.specialist || 'Specialist Doctor') : 'Assign a specialist doctor as the article author' }}
                  </div>
                </div>
              </div>
              <div class="d-flex align-center gap-2">
                <button
                  v-if="selectedDoctor"
                  type="button"
                  class="btn-outline-replace mr-2"
                  style="padding: 4px 10px; font-size: 11px;"
                  @click="removeDoctor"
                >
                  Remove
                </button>
                <button type="button" class="btn-change" @click="isDoctorModalOpen = true">
                  {{ selectedDoctor ? 'Change' : '+ Link' }}
                </button>
              </div>
            </div>
          </div>

          <div class="right-col-divider my-4"></div>

          <div class="status-publish-row mb-4">
            <div class="form-group mb-0">
              <label class="form-label">STATUS</label>
              <div class="select-field-wrap">
                <select v-model="form.status" class="custom-select-box">
                  <option value="published">Published</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="draft">Draft</option>
                  <option value="archived">Archived</option>
                </select>
                <v-icon size="18" class="select-chevron" color="#64748b">mdi-chevron-down</v-icon>
              </div>
            </div>

            <div class="form-group mb-0">
              <label class="form-label">
                {{ form.status === 'scheduled' ? 'AUTO-PUBLISH' : 'PUBLISH DATE & TIME' }}
              </label>

              <v-menu
                v-model="dateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <div class="select-field-wrap" v-bind="attrs" v-on="on" style="cursor: pointer;">
                    <input
                      type="text"
                      readonly
                      :value="displayPublishDateTime"
                      placeholder="DD-MM-YYYY HH:mm"
                      class="custom-select-box"
                      style="cursor: pointer;"
                    />
                    <v-icon size="18" class="select-chevron" color="#64748b">mdi-calendar-blank-outline</v-icon>
                  </div>
                </template>
                <v-card class="pa-2 date-picker-card" style="border-radius: 10px;">
                  <v-date-picker
                    v-model="publishDate"
                    no-title
                    color="#2f6fed"
                  ></v-date-picker>
                  <div class="d-flex align-center justify-space-between px-3 py-2 border-top">
                    <div class="d-flex align-center">
                      <v-icon size="16" color="#64748b" class="mr-2">mdi-clock-outline</v-icon>
                      <input
                        type="time"
                        v-model="publishTime"
                        class="form-input time-popup-input"
                      />
                    </div>
                    <v-btn color="primary" small depressed @click="dateMenu = false">
                      Done
                    </v-btn>
                  </div>
                </v-card>
              </v-menu>
            </div>
          </div>

          <div class="feature-banner-box d-flex align-center justify-space-between mb-4">
            <div>
              <div class="banner-box-title">Feature on Homepage Hero Banner</div>
              <div class="banner-box-sub">Pins this article to the home hero slider</div>
            </div>
            <button
              type="button"
              :class="['ios-switch', { active: form.is_featured }]"
              @click="form.is_featured = !form.is_featured"
            >
              <span class="switch-knob"></span>
            </button>
          </div>
          
          <div class="right-col-divider my-4"></div>

          <div class="seo-card pa-4 mb-2">
            <div class="seo-card-title mb-3">SEO Metadata</div>

            <div class="form-group mb-3">
              <label class="form-sub-label">META TITLE</label>
              <input
                v-model="form.seo_meta_title"
                type="text"
                placeholder="Title tag for search engines..."
                class="form-input"
              />
            </div>

            <div class="form-group mb-3">
              <label class="form-sub-label">META DESCRIPTION</label>
              <textarea
                v-model="form.seo_meta_description"
                rows="2"
                placeholder="Description snippet for search engines..."
                class="form-textarea"
              ></textarea>
            </div>

            <div class="form-group mb-1">
              <label class="form-sub-label">CANONICAL URL</label>
              <input
                v-model="form.canonical_url"
                type="text"
                placeholder="https://www.princhealth.com/news/article-slug"
                class="form-input"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="modal-foot pa-4 d-flex align-center justify-end gap-2">
        <button type="button" class="btn-cancel-draft" :disabled="isSaving" @click="handleClose">
          Cancel
        </button>
        <button type="button" class="btn-cancel-draft" :disabled="isSaving" @click="saveDraft">
          Save Draft
        </button>
        <button type="button" class="btn-primary d-flex align-center" :disabled="isSaving" @click="submitArticle">
          <v-progress-circular
            v-if="isSaving"
            indeterminate
            color="white"
            size="16"
            width="2"
            class="mr-2"
          ></v-progress-circular>
          {{ submitButtonText }}
        </button>
      </div>

      <v-dialog v-model="isDoctorModalOpen" max-width="500">
        <v-card class="pa-4" style="border-radius: 12px;">
          <div class="d-flex align-center justify-space-between mb-3">
            <span class="text-subtitle-1 font-weight-bold">Select Author Doctor</span>
            <v-btn icon small @click="isDoctorModalOpen = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
          <v-text-field
            v-model="doctorSearchText"
            placeholder="Search doctor by name..."
            prepend-inner-icon="mdi-magnify"
            outlined
            dense
            hide-details
            class="mb-3"
          ></v-text-field>
          <v-list style="max-height: 320px; overflow-y: auto;">
            <v-list-item
              v-for="doc in filteredDoctorOptions"
              :key="doc.id"
              link
              @click="selectDoctor(doc)"
            >
              <v-list-item-avatar color="primary" class="white--text">
                {{ getInitials(doc.name) }}
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">{{ doc.name }}</v-list-item-title>
                <v-list-item-subtitle>{{ doc.department || doc.specialist || 'Specialist' }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <div v-if="filteredDoctorOptions.length === 0" class="text-center py-4 grey--text">
              No doctors found.
            </div>
          </v-list>
        </v-card>
      </v-dialog>
    </v-card>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top right :timeout="4000">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">✕</v-btn>
      </template>
    </v-snackbar>
  </v-dialog>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import { quillEditor } from 'vue-quill-editor'
import Vue from 'vue'

export default {
  name: 'CreateNewsArticleDialog',
  components: {
    quillEditor
  },
  props: {
    visible: {
      type: Boolean,
      default: true
    },
    editItem: {
      type: Object,
      default: null
    }
  },
  data() {
    const now = new Date()
    const yyyy = now.getFullYear()
    const mm = String(now.getMonth() + 1).padStart(2, '0')
    const dd = String(now.getDate()).padStart(2, '0')
    const hh = String(now.getHours()).padStart(2, '0')
    const min = String(now.getMinutes()).padStart(2, '0')

    return {
      currentLang: 'th',
      imageFileName: '',
      imageFileSize: '',
      isSaving: false,
      publishDate: `${yyyy}-${mm}-${dd}`,
      publishTime: `${hh}:${min}`,
      dateMenu: false,
      apiErrorMsg: '',
      coverImageError: '',
      snackbar: { show: false, text: '', color: 'success' },

      isDoctorModalOpen: false,
      doctorSearchText: '',
      selectedDoctor: null,

      form: {
        title_th: '',
        title_en: '',
        summary_th: '',
        summary_en: '',
        content_th: '',
        content_en: '',
        slug: '',
        cover_image: '',
        category_id: '',
        org_codename: 'central',
        status: 'published',
        is_featured: false,
        tags: [],
        department_ids: [],
        doctor_ids: [],
        package_ids: [],
        campaign_ids: [],
        seo_meta_title: '',
        seo_meta_description: '',
        canonical_url: ''
      },
      errors: {
        title_th: '',
        content_th: ''
      }
    }
  },
  computed: {
    ...mapState('news', [
      'orgList',
      'category',
      'departmentList',
      'doctorList',
      'packageList',
      'campaignList',
      'isLoadingEntities'
    ]),

    dialogVisible() {
      return this.visible !== undefined ? this.visible : true
    },
    isEditMode() {
      return Boolean(this.editItem && this.editItem.id)
    },
    submitButtonText() {
      if (this.isEditMode) return 'Update Article'
      if (this.form.status === 'scheduled') return 'Schedule Article'
      if (this.form.status === 'draft') return 'Save Draft'
      return 'Publish Article'
    },
    displayPublishDateTime() {
      if (!this.publishDate) return ''
      const parts = this.publishDate.split('-')
      let dateFormatted = this.publishDate
      if (parts.length === 3) {
        dateFormatted = `${parts[2]}-${parts[1]}-${parts[0]}`
      }
      return `${dateFormatted} ${this.publishTime || '00:00'}`
    },

    realBranchList() {
      const allOrgs = (this.orgList && this.orgList.all) || []
      return [
        { name: 'PRINCGroup (Central) — all sites', codename: 'central' },
        ...allOrgs.map(o => ({
          name: (o.name || o.codename || '').toUpperCase(),
          codename: o.codename
        }))
      ]
    },

    realCategoryList() {
      const currentCatId = this.editItem ? String(this.editItem.category_id || (this.editItem.category && this.editItem.category.id) || '') : ''

      const cats = (this.category || []).map(c => ({
        id: String(c.value !== undefined ? c.value : c.id),
        name: c.text || c.name || c.title || `Category ${c.id}`
      }))

      const list = cats.filter(c => {
        const isCurrent = currentCatId && String(c.id) === currentCatId
        const isInactive = (c.name || '').includes('(Inactive)')
        return !isInactive || isCurrent
      })

      if (currentCatId && !list.some(c => String(c.id) === currentCatId)) {
        const catObj = (this.editItem && this.editItem.category) || {}
        const th = (catObj.contents || []).find(c => c.code === 'th')
        const en = (catObj.contents || []).find(c => c.code === 'en')
        const title = (th && th.title) || (en && en.title) || catObj.name || `Category #${currentCatId}`
        list.push({
          id: currentCatId,
          name: title.includes('(Inactive)') ? title : `${title} (Inactive)`
        })
      }

      return list
    },


    filteredDoctorOptions() {
      const list = this.doctorList || []
      if (!this.doctorSearchText || !this.doctorSearchText.trim()) {
        return list
      }
      const q = this.doctorSearchText.trim().toLowerCase()
      return list.filter(d => (d.name || '').toLowerCase().includes(q))
    },

    editorOption() {
      return {
        placeholder: 'Write article body content here...',
        theme: 'snow',
        modules: {
          toolbar: [
            ['bold', 'italic'],
            [{ header: 2 }],
            [{ list: 'bullet' }, { list: 'ordered' }],
            ['image', 'video', 'link']
          ]
        }
      }
    }
  },
  watch: {
    doctorList: {
      handler() {
        if (this.editItem && this.editItem.id) {
          this.resolveAuthorDoctor(this.editItem)
        }
      }
    },
    editItem: {
      immediate: true,
      handler(val) {
        if (val && val.id) {
          this.currentLang = 'th'
          this.resetScroll()
          const th = (val.contents || []).find(c => c.code === 'th') || (val.contents && val.contents[0]) || {}          
          const en = (val.contents || []).find(c => c.code === 'en') || {}
          const rawPubDate = val.public_date || val.publish_date || val.period_start_date
          let pubDateObj = null
          if (rawPubDate) {
            if (val.period_start_time && typeof rawPubDate === 'string' && rawPubDate.length === 10) {
              pubDateObj = new Date(`${rawPubDate}T${val.period_start_time}`)
            } else {
              pubDateObj = new Date(rawPubDate)
            }
          }

          const endDateObj = val.period_end_date ? new Date(val.period_end_date) : null
          const now = new Date()

          const meta = (typeof val.meta === 'string' ? JSON.parse(val.meta || '{}') : (val.meta || {}))

          let statusStr = 'published'
          if (val.status === 1 || val.status === 'draft') {
            if (endDateObj && !isNaN(endDateObj.getTime()) && endDateObj.getTime() < now.getTime()) {
              statusStr = 'archived'
            } else {
              statusStr = 'draft'
            }
          } else {
            if (pubDateObj && !isNaN(pubDateObj.getTime()) && pubDateObj.getTime() > now.getTime()) {
              statusStr = 'scheduled'
            } else if (endDateObj && !isNaN(endDateObj.getTime()) && endDateObj.getTime() < now.getTime()) {
              statusStr = 'archived'
            } else {
              statusStr = 'published'
            }
          }

          let branchCode = 'central'
          if (val.org_codename) {
            branchCode = val.org_codename
          } else if (val.orgs && val.orgs.length === 1) {
            branchCode = val.orgs[0].codename || val.orgs[0].org_codename || 'central'
          }

          if (pubDateObj && !isNaN(pubDateObj.getTime())) {
            const yyyy = pubDateObj.getFullYear()
            const mm = String(pubDateObj.getMonth() + 1).padStart(2, '0')
            const dd = String(pubDateObj.getDate()).padStart(2, '0')
            this.publishDate = `${yyyy}-${mm}-${dd}`

            if (val.period_start_time && typeof val.period_start_time === 'string') {
              this.publishTime = val.period_start_time.substring(0, 5)
            } else {
              const hh = String(pubDateObj.getHours()).padStart(2, '0')
              const min = String(pubDateObj.getMinutes()).padStart(2, '0')
              this.publishTime = `${hh}:${min}`
            }
          }

          const extractIds = (rawList) => {
            if (!rawList || !Array.isArray(rawList)) return []
            return rawList
              .map(item => (typeof item === 'object' && item !== null ? (item.id || item.doctor_id || item.department_id || item.package_id || item.campaign_id) : item))
              .filter(id => id !== undefined && id !== null)
              .map(id => (isNaN(Number(id)) ? id : Number(id)))
          }

          const initialSlug = val.slug || ''
          const canonicalUrl = val.canonical_url || meta.canonical_url || (initialSlug ? `https://www.princhealth.com/news/${initialSlug}` : '')

          this.form = {
            title_th: val.title_th || th.title || '',
            title_en: val.title_en || en.title || '',
            summary_th: (val.summary_th !== undefined && val.summary_th !== null) ? val.summary_th : (th.short_description || ''),
            summary_en: (val.summary_en !== undefined && val.summary_en !== null) ? val.summary_en : (en.short_description || ''),
            content_th: (val.content_th !== undefined && val.content_th !== null) ? val.content_th : (th.description || ''),
            content_en: (val.content_en !== undefined && val.content_en !== null) ? val.content_en : (en.description || ''),
            slug: initialSlug,
            cover_image: val.cover_image || th.thumbnail || th.image || en.thumbnail || '',
            category_id: String(val.category_id || (val.category && val.category.id) || (this.realCategoryList[0] && this.realCategoryList[0].id) || ''),            org_codename: branchCode,
            status: statusStr,
            is_featured: Boolean(val.is_featured),
            tags: Array.isArray(val.tags) ? val.tags.map(t => (typeof t === 'object' && t ? (t.name || t.title) : t)).filter(Boolean) : [],
            department_ids: extractIds(val.department_ids || val.departments),
            doctor_ids: extractIds(val.doctor_ids || val.doctors),
            package_ids: extractIds(val.package_ids || val.packages),
            campaign_ids: extractIds(val.campaign_ids || val.campaigns),
            seo_meta_title: val.seo_meta_title || meta.seo_meta_title || '',
            seo_meta_description: val.seo_meta_description || meta.seo_meta_description || '',
            canonical_url: canonicalUrl
          }

          this.resolveAuthorDoctor(val)
        } else {
          this.resetForm()
        }
      }
    }
  },

  async mounted() {
    await Promise.all([
            !(this.category && this.category.length) && this.fetchCategory ? this.fetchCategory() : null,
      !(this.orgList && this.orgList.all && this.orgList.all.length) && this.setListOrgInfo ? this.setListOrgInfo() : null,
      this.fetchMasterEntities ? this.fetchMasterEntities() : null
    ])

    if (!this.isEditMode) {
      this.resetForm()
    } else if (this.editItem && this.editItem.id) {
      this.resolveAuthorDoctor(this.editItem)
    }
  },
  methods: {
    ...mapActions('news', ['fetchCategory', 'setListOrgInfo', 'fetchMasterEntities', 'saveNewsArticle']),

    resetForm() {
      this.currentLang = 'th'
      this.resetScroll()
      const defaultCatId = (this.realCategoryList && this.realCategoryList[0] && String(this.realCategoryList[0].id)) || ''
      const now = new Date()
      const yyyy = now.getFullYear()
      const mm = String(now.getMonth() + 1).padStart(2, '0')
      const dd = String(now.getDate()).padStart(2, '0')
      const hh = String(now.getHours()).padStart(2, '0')
      const min = String(now.getMinutes()).padStart(2, '0')

      this.form = {
        title_th: '',
        title_en: '',
        summary_th: '',
        summary_en: '',
        content_th: '',
        content_en: '',
        slug: '',
        cover_image: '',
        category_id: defaultCatId,
        org_codename: 'central',
        status: 'published',
        is_featured: false,
        tags: [],
        department_ids: [],
        doctor_ids: [],
        package_ids: [],
        campaign_ids: [],
        seo_meta_title: '',
        seo_meta_description: '',
        canonical_url: ''
      }
      this.publishDate = `${yyyy}-${mm}-${dd}`
      this.publishTime = `${hh}:${min}`
      this.apiErrorMsg = ''
      this.selectedDoctor = null
      this.imageFileName = ''
      this.imageFileSize = ''
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
      this.errors = { title_th: '', content_th: '' }
    },

    handleClose() {
      this.currentLang = 'th'
      this.resetScroll()
      this.resetForm()
      this.$emit('close')
      if (this.$route.name === 'news-create' || this.$route.name === 'news-edit') {
        if (window.history.length > 1) {
          this.$router.back()
        } else {
          this.$router.push('/news-list').catch(() => {})
        }
      }
    },

    slugify(text) {
      if (!text) return ''
      return text
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9ก-๙]+/g, '-')
        .replace(/^-+|-+$/g, '')
    },

    updateSlugAndCanonical() {
      const sourceTitle = (this.form.title_en && this.form.title_en.trim())
        ? this.form.title_en.trim()
        : (this.form.title_th && this.form.title_th.trim() ? this.form.title_th.trim() : '')

      const generated = this.slugify(sourceTitle)
      this.form.slug = generated
      this.form.canonical_url = generated ? `https://www.princhealth.com/news/${generated}` : ''
    },

    onTitleInput(lang) {
      if (lang === 'th') this.errors.title_th = ''
      if (!this.isEditMode || !this.form.slug) {
        this.updateSlugAndCanonical()
      }
    },

    onSlugInput() {
      this.form.slug = this.slugify(this.form.slug)
      this.form.canonical_url = this.form.slug ? `https://www.princhealth.com/news/${this.form.slug}` : ''
    },

    triggerImageUpload() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click()
      }
    },
    onImageSelected(e) {
      const file = e.target.files && e.target.files[0]
      if (file) {
        this.coverImageError = ''
        const MAX_SIZE = 1024 * 1024 

        if (file.size > MAX_SIZE) {
          this.coverImageError = `Image size should be less than 1 MB!`
          if (this.$refs.fileInput) {
            this.$refs.fileInput.value = ''
          }
          return
        }

        this.imageFileName = file.name
        this.imageFileSize = `${Math.round(file.size / 1024)} KB`
        const reader = new FileReader()
        reader.onload = (event) => {
          this.form.cover_image = event.target.result
        }
        reader.readAsDataURL(file)
      }
    },
    removeCoverImage() {
      this.form.cover_image = ''
      this.imageFileName = ''
      this.imageFileSize = ''
      this.coverImageError = ''
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },

    selectDoctor(doc) {
      this.selectedDoctor = doc
      this.isDoctorModalOpen = false
    },
    removeDoctor() {
      this.selectedDoctor = null
    },
    getInitials(name) {
      if (!name || typeof name !== 'string') return ''

      let cleanName = name
        .replace(/^(Dr\.|Dr|นพ\.|นพ|พญ\.|พญ|uw\.|uw)\s*/gi, '')
        .trim()

      if (!cleanName) return name.charAt(0).toUpperCase()

      const parts = cleanName.split(/[\s._-]+/).filter(Boolean)

      if (parts.length >= 2) {
        const firstInitial = parts[0].charAt(0)
        const lastInitial = parts[parts.length - 1].charAt(0)
        return (firstInitial + lastInitial).toUpperCase()
      }

      if (parts.length === 1) {
        return parts[0].charAt(0).toUpperCase()
      }

      return ''
    },


    removeFromList(list, item) {
      const idx = list.indexOf(item)
      if (idx >= 0) list.splice(idx, 1)
    },

    resolveAuthorDoctor(val) {
      if (!val) return
      const authorDocId = val.doctor_id || (val.doctor && (val.doctor.id || val.doctor.doctor_id)) || null
      
      if (authorDocId) {
        const matched = (this.doctorList || []).find(o => String(o.id) === String(authorDocId))
        if (matched) {
          this.selectedDoctor = matched
        } else {
          this.selectedDoctor = {
            id: authorDocId,
            name: (val.doctor && val.doctor.name) || `Doctor #${authorDocId}`,
            department: (val.doctor && val.doctor.department && val.doctor.department.name) || '',            
            department_id: null,
            specialist: 'Specialist Doctor'
          }
        }
      } else {
        this.selectedDoctor = null
      }
    },

    saveDraft() {
      this.form.status = 'draft'
      this.submitArticle()
    },

    async submitArticle() {
      this.errors.title_th = ''
      this.errors.content_th = ''
      this.apiErrorMsg = ''

      let hasError = false
      if (!this.form.title_th || !this.form.title_th.trim()) {
        this.errors.title_th = 'Title (TH) is required'
        hasError = true
      }
      if (!this.form.content_th || !this.form.content_th.trim()) {
        this.errors.content_th = 'Content (TH) is required'
        hasError = true
      }

      if (hasError) {
        this.currentLang = 'th'
        return
      }

      const now = new Date()
      let datePart = this.publishDate
      let timePart = this.publishTime

      if (!datePart) {
        const yyyy = now.getFullYear()
        const mm = String(now.getMonth() + 1).padStart(2, '0')
        const dd = String(now.getDate()).padStart(2, '0')
        datePart = `${yyyy}-${mm}-${dd}`
      }
      if (!timePart) {
        const hh = String(now.getHours()).padStart(2, '0')
        const min = String(now.getMinutes()).padStart(2, '0')
        timePart = `${hh}:${min}`
      }

      let selectedDateTime = new Date(`${datePart}T${timePart}:00`)
      if (isNaN(selectedDateTime.getTime())) {
        selectedDateTime = new Date()
      }

      let statusCode = 0
      let finalPublishDate = selectedDateTime.toISOString()
      let finalEndDate = '2099-12-31T23:59:59.000Z'
      if (this.form.status === 'draft') {
        statusCode = 1
        finalEndDate = '2099-12-31T23:59:59.000Z'
      } else if (this.form.status === 'scheduled') {
        statusCode = 0
        finalEndDate = '2099-12-31T23:59:59.000Z'
        if (selectedDateTime.getTime() <= Date.now()) {
          this.apiErrorMsg = 'For Scheduled articles, the publish date and time must be in the future.'
          return
        }
      } else if (this.form.status === 'archived') {
        statusCode = 1
        const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000)
        finalEndDate = yesterday.toISOString()
      } else {
        statusCode = 0
        finalEndDate = '2099-12-31T23:59:59.000Z'
        if (selectedDateTime.getTime() > Date.now()) {
          finalPublishDate = new Date().toISOString()
        }
      }

      const cleanTags = (this.form.tags || [])
        .map(t => (typeof t === 'string' ? t.trim() : (t.name || t.text || '')))
        .filter(Boolean)

      const docIds = [...(this.form.doctor_ids || [])]
      if (this.selectedDoctor && !docIds.includes(this.selectedDoctor.id)) {
        docIds.push(this.selectedDoctor.id)
      }

      const deptIds = [...(this.form.department_ids || [])]
      if (this.selectedDoctor && this.selectedDoctor.department_id && !deptIds.includes(this.selectedDoctor.department_id)) {        
        deptIds.push(this.selectedDoctor.department_id)
      }

      const payload = {
        title_th: this.form.title_th.trim(),
        title_en: (this.form.title_en && this.form.title_en.trim()) || '',
        summary_th: this.form.summary_th || '',
        summary_en: this.form.summary_en || '',
        content_th: this.form.content_th || '',
        content_en: this.form.content_en || '',
        category_id: Number(this.form.category_id) || 1,
        org_codename: this.form.org_codename || 'central',
        status: statusCode,
        public_date: finalPublishDate,
        publish_date: finalPublishDate,
        period_start_date: datePart,
        period_start_time: timePart.length === 5 ? `${timePart}:00` : timePart,
        period_end_date: finalEndDate,
        period_end_time: this.form.status === 'archived' ? '00:00:00' : '23:59:59',
        doctor_id: this.selectedDoctor ? Number(this.selectedDoctor.id) : null,
        slug: this.form.slug || '',
        cover_image: this.form.cover_image || '',
        is_featured: Boolean(this.form.is_featured),
        tags: cleanTags,
        department_ids: deptIds,
        doctor_ids: docIds,
        package_ids: [...(this.form.package_ids || [])],
        campaign_ids: [...(this.form.campaign_ids || [])],
        meta: {
          seo_meta_title: this.form.seo_meta_title || '',
          seo_meta_description: this.form.seo_meta_description || '',
          canonical_url: this.form.canonical_url || ''
        }
      }

      this.isSaving = true
      try {
        const response = await this.saveNewsArticle({
          isEdit: this.isEditMode,
          id: (this.editItem && this.editItem.id) || this.$route.params.id,          
          payload
        })

        if (response && (response.status === 200 || response.status === 201 || (response.data && response.data.success))) {          
          this.snackbar = { show: true, text: 'Article saved successfully!', color: 'success' }
          this.$emit('saved')
          this.handleClose()
        }
      } catch (err) {
        Vue.$log.error('Failed to save article:', err)
        const errMsg = (err.response && err.response.data && (err.response.data.message || err.response.data.error)) || 'Failed to save article'
        this.apiErrorMsg = errMsg
      } finally {
        this.isSaving = false
      }
    },

    resetScroll() {
      this.$nextTick(() => {
        if (this.$refs.leftCol) this.$refs.leftCol.scrollTop = 0
        if (this.$refs.rightCol) this.$refs.rightCol.scrollTop = 0
        
        const left = document.querySelector('.left-col')
        const right = document.querySelector('.right-col')
        if (left) left.scrollTop = 0
        if (right) right.scrollTop = 0
      })
    }
  }
}
</script>

<style scoped>
::v-deep .article-dialog-window {
  overflow-y: hidden !important;
  border-radius: 14px !important;
}

.article-modal-card {
  display: flex;
  flex-direction: column;
  max-height: 88vh;
  height: 88vh;
  border-radius: 14px !important;
  background-color: #ffffff !important;
  overflow: hidden;
}

.modal-head {
  flex-shrink: 0;
  border-bottom: 1px solid #e6e9f0;
  padding: 16px 22px;
}

.modal-title {
  font-size: 19px !important;
  font-weight: 800 !important;
  line-height: 1.2;
  color: #0f172a !important;
  letter-spacing: -0.2px;
}

.modal-sub {
  font-size: 12.5px;
  color: #64748b;
  line-height: 1.3;
  margin-top: 4px;
}

.close-icon-btn {
  background: #f1f5f9 !important;
  border-radius: 50% !important;
  width: 36px !important;
  height: 36px !important;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s;
}
.close-icon-btn:hover { background-color: #e2e8f0; }

.modal-body-container {
  display: grid;
  grid-template-columns: 1.15fr 1fr;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.left-col,
.right-col {
  padding: 18px 20px;
  min-width: 0;
  overflow-y: auto;
  min-height: 0;
}

.left-col {
  border-right: 1px solid #e6e9f0;
  background: #fbfcfe;
}

.right-col {
  background: #ffffff;
}

.section-badge-label {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.5px;
  color: #64748b;
  text-transform: uppercase;
}

.lang-toggle-wrap {
  display: inline-flex;
  align-items: center;
  background-color: #f1f5f9;
  padding: 3px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.lang-toggle-btn {
  border: none;
  background: transparent;
  padding: 6px 16px;
  border-radius: 6px;
  font-size: 12.5px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  transition: all 0.15s ease;
  outline: none;
}

.lang-toggle-btn.active {
  background-color: #ffffff;
  color: #2563eb;
  font-weight: 700;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
}

.lang-toggle-btn.has-error {
  color: #ef4444;
}

.error-dot {
  color: #ef4444;
  font-size: 10px;
}

.form-label {
  display: block;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.5px;
  color: #64748b;
  margin-bottom: 6px;
  text-transform: uppercase;
}

.required-star {
  color: #ef4444;
  font-weight: 800;
  margin-left: 2px;
}

.form-input,
.form-textarea {
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 9px 12px;
  font-size: 13.5px;
  color: #0f172a;
  outline: none;
  background: #ffffff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.form-input:focus,
.form-textarea:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 1px #2563eb;
}

.input-error {
  border-color: #ef4444 !important;
}

.editor-has-error {
  border-color: #ef4444 !important;
}

.slug-input-wrap {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  height: 42px;
  background: #ffffff;
  transition: border-color 0.15s ease;
}

.slug-input-wrap:focus-within {
  border-color: #2563eb;
  box-shadow: 0 0 0 1px #2563eb;
}

.slug-prefix {
  background: #f8fafc;
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
  padding: 0 14px;
  height: 100%;
  display: flex;
  align-items: center;
  border-right: 1px solid #e2e8f0;
}

.slug-field {
  border: none;
  outline: none;
  font-size: 13.5px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  padding: 0 14px;
  color: #0f172a;
  background: transparent;
}

.cover-image-card {
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  background: #ffffff;
  padding: 12px 16px;
  transition: border-color 0.15s ease;
}

.cover-image-card:hover {
  border-color: #94a3b8;
}
.cover-image-card.card-error {
  border: 1.5px dashed #ef4444 !important;
  background-color: #fef2f2 !important;
}

.image-thumb-box {
  width: 96px;
  height: 54px;
  border-radius: 8px;
  background: linear-gradient(135deg, #1d4ed8 0%, #1e3a8a 100%);
  overflow: hidden;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.1);
}

.image-name {
  font-size: 13.5px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.3;
}

.image-meta {
  font-size: 11.5px;
  color: #64748b;
  margin-top: 3px;
}

.btn-outline-replace {
  border: 1px solid #e2e8f0;
  background: #ffffff;
  border-radius: 8px;
  padding: 6px 16px;
  font-size: 12.5px;
  font-weight: 600;
  color: #1e293b;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  transition: all 0.15s ease;
}

.btn-outline-replace:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.cover-footnote {
  font-size: 11.5px;
  color: #94a3b8;
  font-weight: 500;
}

.form-textarea {
  min-height: 72px;
  line-height: 1.5;
  resize: vertical;
}

.article-quill-editor {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  overflow: hidden;
  transition: border-color 0.2s ease;
}

.article-quill-editor:focus-within {
  border-color: #2563eb;
  box-shadow: 0 0 0 1px #2563eb;
}

.article-quill-editor ::v-deep .ql-toolbar.ql-snow {
  border: none;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
  padding: 8px 10px;
}

.article-quill-editor ::v-deep .ql-container.ql-snow {
  border: none;
  min-height: 170px;
  font-family: inherit;
  font-size: 13.5px;
  color: #0f172a;
}

.article-quill-editor ::v-deep .ql-editor {
  min-height: 170px;
  padding: 12px 14px;
  line-height: 1.6;
}

.article-quill-editor ::v-deep .ql-editor.ql-blank::before {
  color: #94a3b8;
  font-style: normal;
  font-size: 13px;
}

.select-field-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.custom-select-box {
  width: 100%;
  height: 42px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0 32px 0 12px;
  font-size: 13.5px;
  font-weight: 600;
  color: #0f172a;
  background: #ffffff;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.custom-select-box:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 1px #2563eb;
}

.select-chevron {
  position: absolute;
  right: 12px;
  pointer-events: none;
}

.doctor-link-card {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #ffffff;
  padding: 10px 14px;
}

.doctor-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: #dbeafe;
  color: #1e40af;
  font-size: 13px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.avatar-unselected {
  background: #f1f5f9 !important;
  color: #94a3b8 !important;
}

.doctor-avatar-img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.doctor-name {
  font-size: 13.5px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.25;
}

.doctor-dept {
  font-size: 11.5px;
  color: #64748b;
  margin-top: 2px;
}

.btn-change {
  border: 1px solid #e2e8f0;
  background: #ffffff;
  border-radius: 8px;
  padding: 6px 16px;
  font-size: 12.5px;
  font-weight: 600;
  color: #1e293b;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  transition: all 0.15s ease;
}

.btn-change:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.status-publish-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.time-popup-input {
  width: 110px !important;
  height: 34px !important;
  padding: 4px 8px !important;
  font-size: 13px !important;
}

.feature-banner-box {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #ffffff;
  padding: 12px 16px;
}

.banner-box-title {
  font-size: 13.5px;
  font-weight: 700;
  color: #0f172a;
}

.banner-box-sub {
  font-size: 11.5px;
  color: #64748b;
  margin-top: 2px;
}

.ios-switch {
  width: 44px;
  height: 24px;
  border-radius: 24px;
  background-color: #cbd5e1;
  border: none;
  cursor: pointer;
  position: relative;
  transition: background-color 0.2s ease;
  outline: none;
  padding: 2px;
  flex-shrink: 0;
}

.ios-switch.active {
  background-color: #2563eb;
}

.switch-knob {
  display: block;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  transform: translateX(0);
}

.ios-switch.active .switch-knob {
  transform: translateX(20px);
}

.custom-entity-autocomplete {
  border-radius: 8px !important;
}

.custom-entity-autocomplete ::v-deep .v-input__control {
  min-height: 42px !important;
}

.custom-entity-autocomplete ::v-deep .v-input__slot {
  min-height: 42px !important;
  background: #ffffff !important;
  border-radius: 8px !important;
  padding: 3px 12px !important;
  border: 1px solid #e2e8f0 !important;
  display: flex !important;
  align-items: center !important;
  transition: border-color 0.15s ease, box-shadow 0.15s ease !important;
}

.custom-entity-autocomplete ::v-deep fieldset {
  display: none !important;
}

.custom-entity-autocomplete.v-input--is-focused ::v-deep .v-input__slot,
.custom-entity-autocomplete.v-select--is-menu-active ::v-deep .v-input__slot {
  border-color: #2563eb !important;
  box-shadow: 0 0 0 1px #2563eb !important;
}

.custom-entity-autocomplete ::v-deep .v-select__selections {
  min-height: 34px !important;
  display: flex !important;
  align-items: center !important;
  flex-wrap: wrap !important;
  padding: 2px 0 !important;
  gap: 6px !important;
}

.custom-entity-autocomplete ::v-deep input {
  font-size: 13.5px !important;
  color: #0f172a !important;
  line-height: 22px !important;
  height: 26px !important;
  margin: 0 !important;
  padding: 0 2px !important;
  align-self: center !important;
}

.custom-entity-autocomplete ::v-deep input::placeholder {
  color: #94a3b8 !important;
  font-size: 13px !important;
  line-height: 22px !important;
}

.custom-entity-autocomplete ::v-deep .v-input__append-inner {
  align-self: center !important;
  margin-top: 0 !important;
}

.custom-entity-autocomplete ::v-deep .v-input__append-inner .v-icon {
  font-size: 18px !important;
  color: #64748b !important;
}

.custom-keyword-chip {
  background-color: #eff6ff !important;
  color: #1e40af !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  height: 28px !important;
  border-radius: 9999px !important;
  margin: 2px 0 !important;
  padding: 0 12px !important;
  border: 1px solid #dbeafe !important;
}

.custom-keyword-chip ::v-deep .v-chip__close {
  color: #3b82f6 !important;
  font-size: 14px !important;
  margin-left: 6px !important;
}

.right-col-divider {
  border-top: 1px solid #e0e4e8;
}

.seo-card {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #ffffff;
}

.seo-card-title {
  font-size: 13.5px;
  font-weight: 800;
  color: #1e3a8a;
  letter-spacing: -0.2px;
}

.form-sub-label {
  display: block;
  font-size: 10.5px;
  font-weight: 700;
  color: #64748b;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.modal-foot {
  flex-shrink: 0;
  padding: 14px 20px;
  border-top: 1px solid #e6e9f0;
  background-color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}

.btn-cancel-draft {
  border: 1px solid #cbd5e1;
  background: #ffffff;
  border-radius: 8px;
  padding: 8px 18px;
  font-size: 13.5px;
  font-weight: 600;
  color: #334155;
  cursor: pointer;
}

.btn-primary {
  border: none;
  background-color: #2f6fed;
  color: #ffffff;
  border-radius: 8px;
  padding: 9px 20px;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(47, 111, 237, 0.2);
}
.btn-primary:hover { background-color: #2158cf; }

.error-text { font-size: 11.5px; color: #ef4444; }
.gap-2 { gap: 8px; }
.border-top { border-top: 1px solid #e2e8f0; }

.custom-dropdown-menu {
  border-radius: 10px !important;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.12) !important;
  border: 1px solid #e2e8f0 !important;
  background: #ffffff !important;
  max-height: 280px !important;
}

.custom-dropdown-menu .v-list-item {
  min-height: 38px !important;
  padding: 6px 14px !important;
  font-size: 13px !important;
  color: #0f172a !important;
  transition: background-color 0.15s ease;
}

.custom-dropdown-menu .v-list-item:hover {
  background-color: #f8fafc !important;
}

.custom-dropdown-menu .v-list-item--active {
  color: #2563eb !important;
  font-weight: 700 !important;
  background-color: #eff6ff !important;
}
</style>
